/**
 * InsightThesisService
 *
 * The THESES of one pinned insight analysis, read as rows the UI can select
 * from (W13 IS2, AC-W13-06). Until now nothing on the client ever loaded them:
 * `InsightAnalysisSelect` knows analysis metadata, and the packages were read
 * only by the server when it built the generation prompt.
 *
 * Paths (the same two the server projects the prompt from —
 * `builder-service/src/agent/utils/strategyInputManifest.ts`):
 *   projects/{projectId}/insights_analyses/{analysisId}/category_packages/{category}
 *   projects/{projectId}/insights_analyses/{analysisId}/per_task_packages/{taskId}
 *
 * Ruling W13-R22 (binding): the global package and each per-task package come
 * out of their OWN aggregation call with their own model-generated ids, so the
 * two are DISJOINT id namespaces. A row therefore carries the scope it was read
 * from; the wizard narrows — and the server refuses — per scope, never across
 * them.
 *
 * Read-only by design: nothing in the create flow may mutate an analysis.
 */

import { collection, getDocs } from 'firebase/firestore';
import { CoreFirebaseService } from './CoreFirebaseService';

/** One selectable thesis of the pinned analysis. */
export interface InsightThesisRow {
  /** The thesis id the request narrows by (`insightSelection.insightIds`). */
  id: string;
  category: string;
  canonicalDescription: string;
  /** Actors whose items back the thesis — stored, else derived from `sources`. */
  actorCount: number;
  /** The ranking the server's default top-N projection uses. */
  cumulativeWeight: number;
  /** Which package namespace the id belongs to (ruling W13-R22). */
  scope: 'global' | 'task';
  /** Task packages holding this thesis. Empty for a global one. */
  taskIds: string[];
}

/** The thesis universe of one analysis, plus the names its task scopes carry. */
export interface InsightThesisUniverse {
  /** Global theses first, then the per-task ones; each ranked by weight. */
  rows: InsightThesisRow[];
  /** Task id → task name, as the per-task packages recorded it. */
  taskNames: Record<string, string>;
}

/**
 * One CITABLE variant of a thesis (W13 DM2) — the actor's own words behind the
 * quote path `@insight[a].<cat>[<insightId>].sources[<originalItemId>]`.
 *
 * A source WITHOUT an `originalItemId` is dropped: nothing can cite it, so it
 * can never appear in a screen's `dataMapping` and would only pad the payload.
 */
export interface InsightQuoteRow {
  originalItemId: string;
  /** '' when the stored source recorded no actor — the caller decides the copy. */
  actorName: string;
  /** IS3's frozen `originalPhrase`, else the compressed `originalDescription`. */
  phrase: string;
}

/** One thesis as the evidence surfaces read it: its text plus its quotes. */
export interface InsightEvidenceRow {
  id: string;
  category: string;
  canonicalDescription: string;
  quotes: InsightQuoteRow[];
}

/** One raw entry of an `aggregatedInsights` bucket. */
interface StoredInsight {
  id?: unknown;
  category?: unknown;
  canonicalDescription?: unknown;
  actorCount?: unknown;
  frequency?: unknown;
  cumulativeWeight?: unknown;
  sources?: unknown;
}

/** One raw `sources[]` entry (the shape `strategyInputManifest` projects from). */
interface StoredSource {
  actorName?: unknown;
  originalItemId?: unknown;
  originalPhrase?: unknown;
  originalDescription?: unknown;
}

const asNumber = (value: unknown, fallback = 0): number =>
  (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/** Distinct actors behind a thesis, for a document that stored no count. */
function actorCountOf(insight: StoredInsight): number {
  if (typeof insight.actorCount === 'number') return insight.actorCount;
  if (Array.isArray(insight.sources)) {
    const actorIds = new Set(
      insight.sources
        .map((source) => String((source as { actorId?: unknown })?.actorId ?? ''))
        .filter(Boolean),
    );
    if (actorIds.size > 0) return actorIds.size;
  }
  return asNumber(insight.frequency);
}

/**
 * The CITABLE variants of one thesis, in stored order. A source with no
 * `originalItemId` is dropped — no citation can name it (W13 DM1 grammar).
 */
function quotesOf(insight: StoredInsight): InsightQuoteRow[] {
  if (!Array.isArray(insight.sources)) return [];
  const quotes: InsightQuoteRow[] = [];
  for (const raw of insight.sources as StoredSource[]) {
    if (!raw || typeof raw !== 'object') continue;
    const originalItemId = String(raw.originalItemId ?? '');
    if (!originalItemId) continue;
    const phrase = typeof raw.originalPhrase === 'string' && raw.originalPhrase.trim()
      ? raw.originalPhrase.trim()
      : String(raw.originalDescription ?? '');
    quotes.push({ originalItemId, actorName: String(raw.actorName ?? ''), phrase });
  }
  return quotes;
}

/**
 * Every thesis of one `aggregatedInsights` map (`{ [category]: Insight[] }`),
 * flattened. An entry without an id or without the canonical text is DROPPED:
 * the id is what the request narrows by and the text is the whole row — a blank
 * row the operator cannot judge is worse than one row fewer.
 */
function flattenAggregatedInsights(aggregated: unknown): StoredInsight[] {
  if (!aggregated || typeof aggregated !== 'object') return [];
  return Object.values(aggregated as Record<string, unknown>)
    .flatMap((bucket) => (Array.isArray(bucket) ? bucket : []))
    .filter((insight): insight is StoredInsight => Boolean(insight) && typeof insight === 'object')
    .filter((insight) => String(insight.id ?? '').length > 0
      && typeof insight.canonicalDescription === 'string'
      && insight.canonicalDescription.length > 0);
}

export class InsightThesisService {
  private readonly firestore = CoreFirebaseService.getInstance().firestore;

  private readonly projectId: string;

  constructor(projectId: string) {
    this.projectId = projectId;
  }

  private packagesRef(analysisId: string, packageCollection: string) {
    return collection(
      this.firestore,
      'projects',
      this.projectId,
      'insights_analyses',
      analysisId,
      packageCollection,
    );
  }

  /**
   * The ONE read of the two package subcollections — `listTheses` and
   * `listEvidence` are two projections of the same documents, never two reads
   * of two sources.
   */
  private async readPackages(analysisId: string): Promise<{
    categorySnapshot: Awaited<ReturnType<typeof getDocs>>;
    perTaskSnapshot: Awaited<ReturnType<typeof getDocs>>;
  }> {
    const [categorySnapshot, perTaskSnapshot] = await Promise.all([
      getDocs(this.packagesRef(analysisId, 'category_packages')),
      getDocs(this.packagesRef(analysisId, 'per_task_packages')),
    ]);
    return { categorySnapshot, perTaskSnapshot };
  }

  /**
   * Both package subcollections of one analysis, as selectable rows.
   *
   * A read failure PROPAGATES (the caller renders it next to the table): an
   * empty universe would read as «this analysis has no theses», and the step
   * would then send a selection that silently narrows nothing.
   */
  async listTheses(analysisId: string): Promise<InsightThesisUniverse> {
    if (!analysisId) throw new Error('InsightThesisService.listTheses: an analysis id is required');
    try {
      const { categorySnapshot, perTaskSnapshot } = await this.readPackages(analysisId);

      const taskNames: Record<string, string> = {};
      /** id → row, so a thesis several packages hold is ONE row with every scope. */
      const byId = new Map<string, InsightThesisRow>();

      const add = (insight: StoredInsight, scope: 'global' | 'task', taskId?: string): void => {
        const id = String(insight.id);
        const existing = byId.get(id);
        if (existing) {
          if (taskId && !existing.taskIds.includes(taskId)) existing.taskIds.push(taskId);
          return;
        }
        byId.set(id, {
          id,
          category: String(insight.category ?? ''),
          canonicalDescription: String(insight.canonicalDescription),
          actorCount: actorCountOf(insight),
          cumulativeWeight: asNumber(insight.cumulativeWeight),
          scope,
          taskIds: taskId ? [taskId] : [],
        });
      };

      // Global first: its ranking is what the prompt's global section shows.
      const globalRows: InsightThesisRow[] = [];
      for (const doc of categorySnapshot.docs) {
        for (const insight of flattenAggregatedInsights((doc.data() as any)?.aggregatedInsights)) {
          const before = byId.size;
          add(insight, 'global');
          if (byId.size > before) globalRows.push(byId.get(String(insight.id))!);
        }
      }

      const taskRows: InsightThesisRow[] = [];
      for (const doc of perTaskSnapshot.docs) {
        const data = (doc.data() ?? {}) as { taskName?: unknown; aggregatedInsights?: unknown };
        const taskId = doc.id;
        if (typeof data.taskName === 'string' && data.taskName.trim()) {
          taskNames[taskId] = data.taskName.trim();
        }
        for (const insight of flattenAggregatedInsights(data.aggregatedInsights)) {
          const before = byId.size;
          add(insight, 'task', taskId);
          if (byId.size > before) taskRows.push(byId.get(String(insight.id))!);
        }
      }

      const byWeight = (a: InsightThesisRow, b: InsightThesisRow) =>
        b.cumulativeWeight - a.cumulativeWeight;
      return { rows: [...globalRows.sort(byWeight), ...taskRows.sort(byWeight)], taskNames };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightThesisService.listTheses');
      throw error;
    }
  }

  /**
   * The same theses, with the actors' own words attached (W13 DM2).
   *
   * The screen evidence panel and the insight-impact dialog resolve a citation
   * `@insight[<analysisId>].<cat>[<insightId>].sources[<originalItemId>]`
   * through this: the thesis text is the row, the quote is one of its variants.
   * A thesis several packages hold is ONE row, exactly as in `listTheses`.
   *
   * A read failure PROPAGATES: the panel says the analysis could not be read and
   * falls back to the raw citation path — it never renders a blank line.
   */
  async listEvidence(analysisId: string): Promise<InsightEvidenceRow[]> {
    if (!analysisId) throw new Error('InsightThesisService.listEvidence: an analysis id is required');
    try {
      const { categorySnapshot, perTaskSnapshot } = await this.readPackages(analysisId);
      const byId = new Map<string, InsightEvidenceRow>();

      // A thesis the global package and a per-task package both hold is ONE row
      // (as in `listTheses`). The two copies come from the same aggregation and
      // normally carry the SAME `sources[]` — but nothing enforces that, so when
      // they differ the richer document wins: a citation must resolve, and a
      // variant missing from the copy we happened to read first would render as
      // «source not found» for no reason.
      for (const doc of [...categorySnapshot.docs, ...perTaskSnapshot.docs]) {
        for (const insight of flattenAggregatedInsights((doc.data() as any)?.aggregatedInsights)) {
          const id = String(insight.id);
          const quotes = quotesOf(insight);
          const existing = byId.get(id);
          if (existing) {
            if (quotes.length > existing.quotes.length) existing.quotes = quotes;
            continue;
          }
          byId.set(id, {
            id,
            category: String(insight.category ?? ''),
            canonicalDescription: String(insight.canonicalDescription),
            quotes,
          });
        }
      }

      return [...byId.values()];
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'InsightThesisService.listEvidence');
      throw error;
    }
  }
}
